import { FC, FormEvent, ReactElement, RefObject, useEffect, useRef, useState } from 'react';

import { useOutsideClick } from 'raa-react';

import classNames from 'classnames';

interface AutocompleteSelectProps {
  inputElementId?: string;
  disabled?: boolean;
  items: IRaaFilteredSelectItems[];
  onSelect: (item: ISelectableItem) => void;
  placeholderText: string;
  showButton: boolean;
  showButtonText?: string;
  buttonCallback?: () => void;
}

export interface IRaaFilteredSelectItems {
  items: ISelectableItem[];
  name: string[];
  type: string;
  isFritext?: boolean;
  isMatch?(input: string): boolean;
}

export interface ISelectableItem {
  id: string | number;
  name: string;
  relatesToParentName: string;
  type: string;
}

interface AutocompleteSelectListProps {
  hoveredIndex: number | null;
  setHoveredIndex: (index: number | null) => void;
  focusRef: RefObject<HTMLDivElement>;
  setExpanded: (expanded: boolean) => void;
  setSearchQuery: (search: string) => void;
  items: IRaaFilteredSelectItems[];
  updateSelected: (item: ISelectableItem) => void;
  showButton: boolean;
  showButtonText?: string;
  setActiveDescendant: (item: ISelectableItem) => void;
  buttonCallback?: () => void;
}

const AutocompleteSelectList: FC<AutocompleteSelectListProps> = ({
  items,
  hoveredIndex,
  setHoveredIndex,
  focusRef,
  setExpanded,
  setSearchQuery,
  updateSelected,
  showButton,
  showButtonText,
  setActiveDescendant,
  buttonCallback
}: AutocompleteSelectListProps): ReactElement => {
  const [flattenedList, setFlattenedList] = useState<ISelectableItem[]>([]);
  const [hoveredItem, setHoveredItem] = useState<ISelectableItem>();

  const containerRef = useRef<HTMLDivElement>(null);
  const [isOutsideClick] = useOutsideClick(containerRef);

  useEffect(() => {
    setFlattenedList(items.flatMap((filteredSelectItems: IRaaFilteredSelectItems) => filteredSelectItems.items));
  }, [items, setFlattenedList]);

  useEffect(() => {
    if (hoveredIndex !== null) {
      setHoveredItem(flattenedList[hoveredIndex]);
    }
  }, [hoveredIndex, setHoveredItem, flattenedList]);

  useEffect(() => {
    if (hoveredItem) {
      setActiveDescendant(hoveredItem);
    }
  }, [hoveredItem, setActiveDescendant]);

  useEffect(() => {
    if (isOutsideClick) {
      setExpanded(false);
      setSearchQuery('');
    }
  }, [isOutsideClick, items, setExpanded, setSearchQuery]);

  useEffect(() => {
    const focusContainer = focusRef.current;
    const keyPressed = (event: KeyboardEvent) => {
      const listElements = focusContainer?.querySelectorAll('[role="option"]');

      if (event.key === 'Escape') {
        setExpanded(false);
        setSearchQuery('');
        return;
      }
      if (!listElements?.length) return;

      if (event.key === 'Tab') {
        setExpanded(false);
      }

      if (event.key === 'ArrowDown') {
        if (hoveredIndex === null) {
          setHoveredIndex(0);
          const focusedElement = listElements[0] as HTMLLIElement;
          focusedElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
        } else if (hoveredIndex < listElements.length - 1) {
          setHoveredIndex(hoveredIndex + 1);
          const focusedElement = listElements[hoveredIndex + 1] as HTMLLIElement;
          focusedElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
        } else if (hoveredIndex === listElements.length - 1 && showButton) {
          setHoveredIndex(null);
          setHoveredItem(undefined);
          const buttonContainer = focusContainer?.querySelector('#buttonContainer');
          const focusedElement = buttonContainer as HTMLDivElement;
          focusedElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      } else if (event.key === 'ArrowUp') {
        if (hoveredIndex === null) {
          setHoveredIndex(flattenedList.length - 1);
          const focusedElement = listElements[flattenedList.length - 1] as HTMLLIElement;
          focusedElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
        } else if (hoveredIndex > 0) {
          setHoveredIndex(hoveredIndex - 1);
          const focusedElement = listElements[hoveredIndex - 1] as HTMLLIElement;
          focusedElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
        } else if (hoveredIndex === 0) {
          setHoveredIndex(0);
          const focusedElement = listElements[0] as HTMLLIElement;
          focusedElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (hoveredIndex !== null) {
          updateSelected(flattenedList[hoveredIndex]);
        } else if (showButton && hoveredIndex === null) {
          buttonCallback?.();
        }
        setExpanded(false);
        setSearchQuery('');
      }
    };
    focusContainer?.addEventListener('keydown', keyPressed);
    return () => {
      focusContainer?.removeEventListener('keydown', keyPressed);
    };
  }, [
    hoveredIndex,
    setExpanded,
    setHoveredIndex,
    flattenedList,
    focusRef,
    updateSelected,
    setSearchQuery,
    showButton,
    buttonCallback
  ]);

  return (
    <div className="tw-relative">
      <div
        ref={containerRef}
        role="listbox"
        id="listbox"
        className="tw-absolute tw-left-0 tw-top-0 tw-z-20 tw-max-h-[25rem] tw-w-[99.5%] tw-overflow-y-auto tw-border tw-border-t-0 tw-border-raa-gray-3 tw-bg-raa-white"
      >
        {items.map(
          (item, index) =>
            item.items.length > 0 && (
              <div key={index} className="tw-flex tw-w-full tw-flex-col tw-justify-between">
                <div className="tw-mx-4 tw-flex">
                  {item.name.length && <p className="tw-w-1/2 tw-py-2">{item.name[0]}</p>}
                  {item.name.length > 1 && <p className="tw-w-1/2 tw-py-2">{item.name[1]}</p>}
                </div>
                <hr className="tw-mx-4 tw-border tw-border-raa-gray-5" />
                <ul className="item-list" data-testid="autocompleteSelect-item-list">
                  {item.items.map((itemChild, index) => (
                    <li
                      id={itemChild.id.toString()}
                      onMouseOver={() => {
                        setHoveredItem(itemChild);
                        const i = flattenedList.findIndex((element) => element.id === itemChild.id);
                        setHoveredIndex(i);
                      }}
                      onClick={() => updateSelected(itemChild)}
                      className={classNames('tw-flex tw-p-4 tw-outline-none', {
                        'tw-bg-raa-gray-2': itemChild.id === hoveredItem?.id && hoveredIndex !== null
                      })}
                      key={index}
                      aria-selected={itemChild.id === hoveredItem?.id}
                      role="option"
                      data-testid={itemChild.name}
                    >
                      <span className="tw-flex tw-flex-1 tw-font-semibold">
                        {itemChild.name} <span className="tw-sr-only">{' ' + item.name[0]}</span>
                      </span>
                      <span className="tw-flex tw-flex-1">
                        {itemChild.relatesToParentName}
                        {itemChild.relatesToParentName && <span className="tw-sr-only">{' ' + item.name[1]}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )
        )}
        {!flattenedList.length && <span className="tw-m-8 tw-block tw-text-center tw-italic">inget resultat</span>}
        {showButton && (
          <div
            onMouseOver={() => setHoveredItem(undefined)}
            id="buttonContainer"
            className={classNames(
              'tw-flex tw-w-full tw-border-t tw-border-raa-gray-4 tw-py-6 hover:tw-cursor-pointer hover:tw-bg-raa-gray-2',
              {
                'tw-bg-raa-gray-2': hoveredIndex === null
              }
            )}
          >
            <button role="option" onClick={buttonCallback} className="tw-ml-4">
              {showButtonText}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const AutocompleteSelect: FC<AutocompleteSelectProps> = (props: AutocompleteSelectProps): ReactElement => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [filteredItems, setFilteredItems] = useState<IRaaFilteredSelectItems[]>([]);
  const [activedescendant, setActiveDescendant] = useState<ISelectableItem>();

  const focusRef = useRef<HTMLDivElement>(document.createElement('div'));
  const inputRef = useRef<HTMLInputElement>(document.createElement('input'));

  useEffect(() => {
    const regex = new RegExp(`^${searchQuery}`, 'i');
    const list = props.items.map((filteredSelectItems: IRaaFilteredSelectItems) => ({
      ...filteredSelectItems,
      items: filteredSelectItems.items.filter((selectableItem: ISelectableItem) => selectableItem.name.match(regex))
    }));
    setFilteredItems(list);
    setExpanded(searchQuery.length > 0);
    if (!searchQuery.length) {
      setHoverIndex(null);
    }
    if (searchQuery.length) {
      setHoverIndex(0);
    }
  }, [searchQuery, setFilteredItems, setHoverIndex, props.items]);

  const handleClick = (item: ISelectableItem): void => {
    props.onSelect(item);
    setExpanded(false);
    setSearchQuery('');
  };

  return (
    <div className="tw-border tw-border-raa-gray-3" ref={focusRef}>
      <input
        disabled={props.disabled}
        ref={inputRef}
        value={searchQuery}
        onChange={(event: FormEvent<HTMLInputElement>) => setSearchQuery((event.target as HTMLInputElement).value)}
        id={props.inputElementId}
        type="text"
        autoComplete="off"
        placeholder={props.placeholderText}
        className="raa-input tw-border-0"
        aria-expanded={expanded}
        role="combobox"
        aria-controls="listbox"
        aria-activedescendant={activedescendant?.id.toString() ?? ''}
      />
      {expanded && (
        <AutocompleteSelectList
          items={filteredItems}
          hoveredIndex={hoverIndex}
          setHoveredIndex={setHoverIndex}
          showButton={props.showButton}
          showButtonText={props.showButtonText}
          focusRef={focusRef}
          setSearchQuery={setSearchQuery}
          setExpanded={setExpanded}
          updateSelected={(item: ISelectableItem) => handleClick(item)}
          setActiveDescendant={setActiveDescendant}
          buttonCallback={props.buttonCallback}
        />
      )}
    </div>
  );
};

export default AutocompleteSelect;
